/**
 * Shared PXL widgets. Deliberately placeholder: missing art renders as a labelled
 * colour glyph (IconGlyph) keyed to a Master Asset Register row, so every surface
 * is testable before real icons exist. Pure presentation.
 */

import type { ReactNode } from 'react';

/** Stable hue from any id string, so the same item always gets the same glyph colour. */
function hueOf(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

/**
 * A placeholder icon: a coloured tile with 1–2 letters, deterministically derived
 * from `code`. `assetRef` (a Master Asset Register id) is shown on hover so the
 * art dependency is traceable.
 */
export function IconGlyph({
  code,
  label,
  assetRef,
  size = 34,
}: {
  code: string;
  label?: string;
  assetRef?: string;
  size?: number;
}): JSX.Element {
  const hue = hueOf(code || '?');
  const letters =
    (label ?? code ?? '?')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase() || '?';
  return (
    <span
      className="pxl-glyph"
      title={`${label ?? code}${assetRef ? ` · needs ${assetRef}` : ''}`}
      style={{
        width: size,
        height: size,
        background: `hsl(${hue} 55% 62%)`,
        fontSize: Math.round(size * 0.36),
      }}
    >
      {letters}
    </span>
  );
}

export function Bar({
  value,
  max,
  color,
  label,
  height = 14,
}: {
  value: number;
  max: number;
  color?: string;
  label?: string;
  height?: number;
}): JSX.Element {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <div className="pxl-bar" style={{ height }}>
      <div className="fill" style={{ width: `${(pct * 100).toFixed(1)}%`, background: color }} />
      {label !== undefined ? <div className="label">{label}</div> : null}
    </div>
  );
}

export interface TabDef {
  id: string;
  label: string;
}

export function Tabs({
  tabs,
  active,
  onSelect,
}: {
  tabs: TabDef[];
  active: string;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="pxl-tabs">
      {tabs.map((t) => (
        <button
          key={t.id}
          className={`pxl-tab${t.id === active ? ' active' : ''}`}
          onClick={() => onSelect(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Stat({ k, v }: { k: string; v: ReactNode }): JSX.Element {
  return (
    <div className="pxl-stat">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <div className="pxl-empty">{children}</div>;
}

export function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return <div className="pxl-section-title">{children}</div>;
}

/** A Shard or Gleam amount pill (the AD-024 wall: never the same widget for both). */
export function Money({
  amount,
  kind,
}: {
  amount: number | null | undefined;
  kind: 'shard' | 'gleam';
}): JSX.Element {
  const sym = kind === 'shard' ? '◈' : '✦';
  const color = kind === 'shard' ? 'var(--pxl-shard)' : 'var(--pxl-gleam)';
  return (
    <span
      style={{ color, fontVariantNumeric: 'tabular-nums' }}
      title={kind === 'shard' ? 'Shard (soft currency)' : 'Gleam (premium · cosmetic-only)'}
    >
      {sym} {amount ?? '—'}
    </span>
  );
}

/**
 * Generic read-only renderer for any wire payload — used by panels whose backend
 * shape is faithfully surfaced but not yet given bespoke art (breadth-first). It
 * shows the real server truth, which is exactly what gameplay testing needs.
 */
export function DataView({ value }: { value: unknown }): JSX.Element {
  let text: string;
  try {
    text = value === undefined ? '(none)' : JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return <pre className="pxl-dataview">{text}</pre>;
}

/** A labelled text/number input row. */
export function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  width,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  width?: number;
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
      {label ? <span className="pxl-muted">{label}</span> : null}
      <input
        className="pxl-input"
        type={type}
        value={value}
        placeholder={placeholder}
        style={width ? { width } : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
